import React, { useState, useEffect } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import axios from "axios";
import { backendURL } from "../config";
import {
    ShieldAlert, ArrowRight, CheckCircle2, Clock, Bot,
    Sparkles, Plus, ChevronRight, Check, AlertTriangle, Shield,
    Cpu, Globe, Terminal, Play, Zap, RefreshCw, X, Radio,
    FileCode, Sliders, ArrowUpRight, BarChart2, Search,
    Filter, Lock, Server, Users, TrendingUp,
    CheckCircle, AlertCircle, ArrowUp, ArrowDown, Activity,
    Calendar, Layers, FileCheck, ExternalLink, ChevronDown,
    Flame, Database, HelpCircle, HardDrive, User, Network,
    Upload, MessageSquare, ShieldCheck, CornerDownRight, Copy
} from "lucide-react";
import AbuseIPDBPopover from "../components/AbuseIPDBPopover";

export default function CaseRoom() {
    const { caseId } = useParams();
    const navigate = useNavigate();
    const [caseData, setCaseData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [activeTab, setActiveTab] = useState("findings");
    const [toast, setToast] = useState(null);
    const [actionsDropdownOpen, setActionsDropdownOpen] = useState(false);
    const [isLocked, setIsLocked] = useState(false);
    const [copilotOpen, setCopilotOpen] = useState(false);
    const [newNote, setNewNote] = useState("");
    const [osintIp, setOsintIp] = useState("");
    const [osintResult, setOsintResult] = useState(null);
    const [osintLoading, setOsintLoading] = useState(false);

    const token = localStorage.getItem("token");
    const headers = { Authorization: `Bearer ${token}` };

    const runOsintLookup = async (targetIp) => {
        const query = targetIp || osintIp;
        if (!query || !query.trim()) return;
        setOsintLoading(true);
        try {
            const res = await axios.get(`${backendURL}/api/soc/mxdr/enrich?ip=${encodeURIComponent(query.trim())}`);
            if (res.data && res.data.data) {
                setOsintResult(res.data.data);
            }
        } catch (err) {
            console.error("OSINT error:", err);
        } finally {
            setOsintLoading(false);
        }
    };

    useEffect(() => {
        fetchCaseDetails();
    }, [caseId]);

    const fetchCaseDetails = async () => {
        setLoading(true);
        try {
            const url = caseId ? `${backendURL}/api/respond/case/${caseId}` : `${backendURL}/api/respond/case`;
            const res = await axios.get(url, { headers });
            setCaseData(res.data);
        } catch (err) {
            if (err.response?.status === 401) {
                localStorage.removeItem("token");
                window.location.href = "/login";
                return;
            }
            console.error("Failed to load case:", err);
        }
        setLoading(false);
    };

    const handleExecuteAction = async (actionType, taskId = null) => {
        try {
            const url = caseId ? `${backendURL}/api/respond/case/${caseId}/action` : `${backendURL}/api/respond/case/action`;
            const res = await axios.post(url, { actionType, taskId }, { headers });
            if (res.data.success) {
                if (taskId) {
                    setCaseData(prev => ({
                        ...prev,
                        remediationTasks: prev.remediationTasks.map(t => t.id === taskId ? { ...t, completed: true } : t)
                    }));
                }
                setToast(res.data.message || `Action ${actionType} executed!`);
                setTimeout(() => setToast(null), 3000);
            }
        } catch (err) {
            console.error("Failed to execute action:", err);
        }
        setActionsDropdownOpen(false);
    };

    const handleAddNote = (e) => {
        e.preventDefault();
        if (!newNote.trim()) return;
        const noteObj = {
            id: "nt-" + Date.now(),
            author: "SecOps Analyst (You)",
            time: new Date().toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" }),
            text: newNote
        };
        setCaseData(prev => ({
            ...prev,
            notes: [noteObj, ...(prev.notes || [])]
        }));
        setNewNote("");
        setToast("Note added to investigation evidence ledger");
        setTimeout(() => setToast(null), 2500);
    };

    const copyToClipboard = (text) => {
        navigator.clipboard.writeText(text);
        setToast("Copied to clipboard!");
        setTimeout(() => setToast(null), 2000);
    };

    if (loading && !caseData) {
        return (
            <div className="min-h-[400px] flex items-center justify-center">
                <div className="flex items-center gap-2.5 text-slate-400 font-mono text-xs">
                    <RefreshCw className="w-4 h-4 animate-spin text-blue-500" />
                    <span>Loading authentic case telemetry...</span>
                </div>
            </div>
        );
    }

    if (!caseData) {
        return (
            <div className="p-12 text-center rounded-2xl bg-[#0d1424] border border-white/[0.06] space-y-3">
                <ShieldAlert className="w-8 h-8 text-amber-400 mx-auto" />
                <h3 className="text-base font-bold text-slate-100">Case Not Found</h3>
                <p className="text-xs text-slate-400">The requested investigation case does not exist or has been archived.</p>
                <Link to="/cases" className="inline-block px-4 py-2 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-lg">Return to Cases Board</Link>
            </div>
        );
    }

    const c = caseData;

    return (
        <div className="w-full space-y-6 pb-12">
            {/* ─── Toast Feedback ─── */}
            {toast && (
                <div className="fixed bottom-6 right-6 z-50 bg-blue-600 text-white px-4 py-2.5 rounded-xl shadow-2xl text-[12px] font-bold flex items-center gap-2 animate-in fade-in slide-in-from-bottom-4 duration-200">
                    <Check className="w-4 h-4" />
                    {toast}
                </div>
            )}

            {/* ─── Breadcrumb & Top Action Toolbar (Matching Screenshot 01) ─── */}
            <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 border-b border-white/[0.06] pb-4">
                <div className="flex items-center gap-2 text-[11.5px] font-mono text-slate-400">
                    <Link to="/cases" className="hover:text-white transition-colors">Respond</Link>
                    <span>&gt;</span>
                    <Link to="/cases" className="hover:text-white transition-colors">Cases</Link>
                    <span>&gt;</span>
                    <span className="text-white font-bold">{c.title}</span>
                </div>

                <div className="flex items-center gap-2.5 relative">
                    {/* Collaborate with QueryTel */}
                    <button
                        onClick={() => setToast("Opening QueryTel Autonomous Co-Investigator chat...")}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 hover:text-white border border-white/[0.08] rounded-lg text-[11.5px] font-mono transition-all"
                    >
                        <Bot className="w-3.5 h-3.5 text-purple-400" />
                        Collaborate with QueryTel
                    </button>

                    {/* Lock Case */}
                    <button
                        onClick={() => {
                            setIsLocked(!isLocked);
                            setToast(isLocked ? "Case unlocked" : "Case locked for forensic integrity");
                            setTimeout(() => setToast(null), 2500);
                        }}
                        className={`flex items-center gap-1.5 px-3 py-1.5 border rounded-lg text-[11.5px] font-mono transition-all ${
                            isLocked ? "bg-amber-500/20 text-amber-300 border-amber-500/40 font-bold" : "bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 border-white/[0.08]"
                        }`}
                    >
                        <Lock className="w-3.5 h-3.5" />
                        {isLocked ? "Locked" : "Lock case"}
                    </button>

                    {/* Upload File */}
                    <button
                        onClick={() => setToast("Evidence upload pipeline ready")}
                        className="flex items-center gap-1.5 px-3 py-1.5 bg-white/[0.04] hover:bg-white/[0.08] text-slate-300 hover:text-white border border-white/[0.08] rounded-lg text-[11.5px] font-mono transition-all"
                    >
                        <Upload className="w-3.5 h-3.5" />
                        Upload file
                    </button>

                    {/* Actions (5) Dropdown */}
                    <div className="relative">
                        <button
                            onClick={() => setActionsDropdownOpen(!actionsDropdownOpen)}
                            className="flex items-center gap-1.5 px-3.5 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[11.5px] font-bold shadow-md shadow-blue-600/20 transition-all"
                        >
                            Actions (5)
                            <ChevronDown className="w-3.5 h-3.5" />
                        </button>

                        {actionsDropdownOpen && (
                            <div className="absolute right-0 mt-1 w-56 bg-[#0F172A] border border-white/[0.1] rounded-xl shadow-2xl z-50 py-1.5 text-[11.5px] font-mono animate-in fade-in zoom-in-95 duration-100">
                                {[
                                    { label: "⚡ Isolate Host ('desktop-PC')", action: "Isolate Host" },
                                    { label: "🛑 Terminate Process (PID 4812)", action: "Kill Process" },
                                    { label: "🔑 Revoke Okta Admin Tokens", action: "Revoke Tokens" },
                                    { label: "🧱 Block C2 IP (185.220.101.44)", action: "Block IP" },
                                    { label: "📑 Export Forensic DFIR Report", action: "Export DFIR" }
                                ].map((act, idx) => (
                                    <button
                                        key={idx}
                                        onClick={() => handleExecuteAction(act.action)}
                                        className="w-full text-left px-3 py-2 text-slate-300 hover:text-white hover:bg-white/[0.06] transition-colors flex items-center justify-between"
                                    >
                                        <span>{act.label}</span>
                                    </button>
                                ))}
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {/* ─── MAIN CASE ROOM 2-COLUMN LAYOUT (Matching Screenshot 01) ─── */}
            <div className="grid grid-cols-1 lg:grid-cols-12 gap-6 items-start">
                
                {/* ═══ LEFT CASE SUMMARY COLUMN (4 cols) ═══ */}
                <div className="lg:col-span-4 bg-[#111827] border border-white/[0.06] rounded-2xl p-5 shadow-xl space-y-5">
                    <div>
                        <h2 className="text-[18px] font-black text-white tracking-tight leading-snug">
                            {c.title}
                        </h2>
                    </div>

                    {/* Metadata Grid */}
                    <div className="space-y-2.5 text-[11.5px] font-mono border-t border-b border-white/[0.06] py-3.5">
                        <div className="flex items-center justify-between">
                            <span className="text-slate-500">Created time</span>
                            <span className="text-slate-300">{c.createdTime}</span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-slate-500">Status</span>
                            <span className="flex items-center gap-1.5 text-amber-400 font-bold">
                                <div className="w-1.5 h-1.5 rounded-full bg-amber-400 animate-pulse" />
                                {c.status}
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-slate-500">Severity</span>
                            <span className="px-2 py-0.5 rounded bg-rose-500/20 text-rose-400 font-bold text-[10px]">
                                {c.severity}
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-slate-500">QueryTel Verdict</span>
                            <span className="px-2 py-0.5 rounded bg-red-600 text-white font-black text-[10px] tracking-wider">
                                {c.verdict}
                            </span>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-slate-500">Tags</span>
                            <div className="flex items-center gap-1.5">
                                {(c.tags || ["Network: 2", "Endpoint: 1"]).map((tag, idx) => (
                                    <span key={idx} className="px-1.5 py-0.2 bg-white/[0.04] text-slate-300 rounded text-[9.5px] border border-white/[0.06]">
                                        {tag}
                                    </span>
                                ))}
                            </div>
                        </div>
                        <div className="flex items-center justify-between">
                            <span className="text-slate-500">Assignee</span>
                            <span className="text-slate-400">{c.assignee}</span>
                        </div>
                    </div>

                    {/* QueryTel Case Summary Block */}
                    <div className="space-y-2">
                        <span className="text-[10.5px] font-mono uppercase font-bold text-slate-400 flex items-center gap-1.5">
                            <Sparkles className="w-3.5 h-3.5 text-purple-400" />
                            QueryTel Case Summary
                        </span>
                        <p className="text-[11.5px] text-slate-300 leading-relaxed bg-black/40 p-3.5 rounded-xl border border-white/[0.04] font-sans">
                            {c.summary}
                        </p>
                    </div>

                    {/* Quick Response Containment CTA */}
                    <div className="p-3 bg-rose-950/20 border border-rose-500/30 rounded-xl space-y-2">
                        <span className="text-[10px] font-mono uppercase text-rose-400 font-bold block">
                            Active Threat Containment
                        </span>
                        <button
                            onClick={() => handleExecuteAction("Isolate Host")}
                            className="w-full py-2 bg-rose-600 hover:bg-rose-500 text-white rounded-lg text-[11px] font-bold shadow-md shadow-rose-600/20 transition-all font-mono"
                        >
                            🛑 1-Click Isolate 'desktop-PC'
                        </button>
                    </div>
                </div>

                {/* ═══ RIGHT CASE WORKBENCH: 6 TABS (8 cols) ═══ */}
                <div className="lg:col-span-8 bg-[#111827] border border-white/[0.06] rounded-2xl p-6 shadow-2xl space-y-5">
                    
                    {/* Tabs Ribbon matching screenshot 01 */}
                    <div className="flex flex-wrap items-center gap-6 border-b border-white/[0.06] pb-3 text-[12px] font-mono">
                        {[
                            { id: "remediation", label: "Remediation" },
                            { id: "findings", label: "Key findings" },
                            { id: "artifacts", label: "Artifacts" },
                            { id: "alerts", label: "Incident & alerts" },
                            { id: "notes", label: "Notes & Evidence" },
                            { id: "history", label: "Case History" }
                        ].map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`pb-2 transition-all relative ${
                                    activeTab === tab.id ? "text-white font-bold" : "text-slate-500 hover:text-slate-300"
                                }`}
                            >
                                {tab.label}
                                {activeTab === tab.id && (
                                    <div className="absolute bottom-[-13px] left-0 right-0 h-0.5 bg-blue-500 rounded-full" />
                                )}
                            </button>
                        ))}
                    </div>

                    {/* ─── TAB 1: REMEDIATION ─── */}
                    {activeTab === "remediation" && (
                        <div className="space-y-4 animate-in fade-in duration-150">
                            <div className="flex items-center justify-between">
                                <span className="text-[12px] font-bold text-white font-mono uppercase">
                                    Containment & Eradication Playbook Tasks
                                </span>
                                <span className="text-[11px] font-mono text-emerald-400">
                                    {(c.remediationTasks || []).filter(t => t.completed).length} of {(c.remediationTasks || []).length} Completed
                                </span>
                            </div>

                            <div className="space-y-2.5">
                                {(c.remediationTasks || []).map((task) => (
                                    <div
                                        key={task.id}
                                        className={`p-3 rounded-xl border flex items-center justify-between gap-3 text-[11.5px] transition-all ${
                                            task.completed ? "bg-emerald-950/10 border-emerald-500/30 opacity-70" : "bg-black/40 border-white/[0.04] hover:border-white/10"
                                        }`}
                                    >
                                        <div className="flex items-center gap-2.5">
                                            <div className={`w-4 h-4 rounded-md flex items-center justify-center ${task.completed ? "bg-emerald-500 text-white" : "border border-slate-600"}`}>
                                                {task.completed && <Check className="w-3 h-3" />}
                                            </div>
                                            <span className={`font-medium ${task.completed ? "line-through text-slate-400" : "text-white"}`}>
                                                {task.title}
                                            </span>
                                        </div>

                                        {!task.completed && (
                                            <button
                                                onClick={() => handleExecuteAction(task.action, task.id)}
                                                className="px-3 py-1 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[10.5px] font-bold font-mono shadow transition-all"
                                            >
                                                Execute {task.action} →
                                            </button>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ─── TAB 2: KEY FINDINGS (Matching Screenshot 01) ─── */}
                    {activeTab === "findings" && (
                        <div className="space-y-5 animate-in fade-in duration-150">
                            
                            {/* Key Artifacts Top Indicators */}
                            <div className="space-y-2">
                                <span className="text-[10.5px] font-mono uppercase text-slate-500 block">
                                    Key artifacts
                                </span>
                                <div className="flex flex-wrap items-center gap-3">
                                    {/* Device */}
                                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-black/40 border border-rose-500/30 text-white text-[11.5px] font-mono">
                                        <div className="w-2 h-2 rounded-full bg-rose-500 animate-ping" />
                                        <span>Device: <strong>{c.keyArtifacts?.device?.name || "desktop-PC"}</strong></span>
                                    </div>

                                    {/* File */}
                                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-black/40 border border-rose-500/30 text-white text-[11.5px] font-mono">
                                        <div className="w-2 h-2 rounded-full bg-rose-500 animate-ping" />
                                        <span>File: <strong>winlnk.exe.lnk</strong></span>
                                    </div>

                                    {/* Timeline summary */}
                                    <div className="flex items-center gap-2 px-3 py-1.5 rounded-xl bg-black/40 border border-white/[0.08] text-slate-300 text-[11.5px] font-mono">
                                        <Clock className="w-3.5 h-3.5 text-blue-400" />
                                        <span>Timeline summary (4 events)</span>
                                    </div>
                                </div>
                            </div>

                            {/* Alert #1 Container Box matching screenshot 01 */}
                            <div className="bg-black/40 border border-white/[0.06] rounded-2xl p-5 space-y-4">
                                <div className="flex items-center justify-between border-b border-white/[0.06] pb-3">
                                    <span className="text-[12px] font-bold text-white font-mono">
                                        Alert #1: "{c.title}"
                                    </span>
                                    <span className="text-[10px] font-mono text-slate-500">
                                        CrowdStrike Falcon Sensor / Microsoft Defender ATP
                                    </span>
                                </div>

                                <div className="grid grid-cols-1 md:grid-cols-12 gap-5 items-start">
                                    
                                    {/* Left Artifact Details (8 cols) */}
                                    <div className="md:col-span-8 space-y-4">
                                        
                                        {/* Device Spec */}
                                        <div className="space-y-1.5 text-[11.5px]">
                                            <div className="flex items-center gap-2 font-mono text-rose-400 font-bold">
                                                <HardDrive className="w-3.5 h-3.5" />
                                                <span>Device: {c.keyArtifacts?.device?.name || "desktop-PC"}</span>
                                            </div>
                                            <div className="bg-black/30 p-2.5 rounded-xl border border-white/[0.04] text-[11px] font-mono text-slate-300 space-y-1">
                                                <div>OS: {c.keyArtifacts?.device?.os}</div>
                                                <div>Private IP: {c.keyArtifacts?.device?.ip} • Domain: {c.keyArtifacts?.device?.domain}</div>
                                                <div className="text-amber-400">{c.keyArtifacts?.device?.anomalies30d}</div>
                                            </div>
                                        </div>

                                        {/* File Spec */}
                                        <div className="space-y-1.5 text-[11.5px]">
                                            <div className="flex items-center gap-2 font-mono text-rose-400 font-bold">
                                                <FileCode className="w-3.5 h-3.5" />
                                                <span>File: {c.keyArtifacts?.file?.path}</span>
                                            </div>
                                            <div className="bg-black/30 p-2.5 rounded-xl border border-white/[0.04] text-[10.5px] font-mono text-slate-300 space-y-1.5">
                                                <div className="flex items-center justify-between text-slate-400">
                                                    <span className="truncate">SHA256: {c.keyArtifacts?.file?.sha256}</span>
                                                    <button onClick={() => copyToClipboard(c.keyArtifacts?.file?.sha256)} className="text-blue-400 hover:text-blue-300 ml-2">
                                                        <Copy className="w-3 h-3" />
                                                    </button>
                                                </div>
                                                <div className="text-slate-400">
                                                    Process Tree: <span className="text-white">{c.keyArtifacts?.file?.processTree}</span>
                                                </div>
                                            </div>
                                        </div>

                                        {/* Timeline summary */}
                                        <div className="space-y-2 text-[11.5px]">
                                            <span className="font-mono text-slate-400 uppercase font-bold text-[10.5px] block">
                                                Timeline summary
                                            </span>
                                            <div className="space-y-2 border-l border-white/[0.08] ml-2 pl-3">
                                                {(c.keyArtifacts?.timeline || []).map((tl, idx) => (
                                                    <div key={idx} className="space-y-0.5 relative">
                                                        <div className="absolute -left-[17px] top-1 w-2 h-2 rounded-full bg-blue-500" />
                                                        <div className="flex items-center gap-2">
                                                            <span className="text-[10px] font-mono text-slate-500">{tl.time}</span>
                                                            <span className="text-[9.5px] font-mono text-purple-400 bg-purple-500/10 px-1.5 rounded">{tl.actor}</span>
                                                        </div>
                                                        <p className="text-[11px] text-slate-300">{tl.text}</p>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                    </div>

                                    {/* Right Detail Card (4 cols) */}
                                    <div className="md:col-span-4 bg-black/50 p-4 rounded-xl border border-white/[0.06] space-y-3">
                                        <span className="text-[11px] font-mono uppercase font-bold text-slate-400 block">
                                            Evidence Synthesis
                                        </span>
                                        <p className="text-[11px] text-slate-300 leading-relaxed">
                                            The investigation examined a suspicious file on <strong>desktop-PC</strong>, flagged by Crowdstrike/Defender. The file, found in the <code>C:\Users\Admin\AppData\Local\Temp</code> directory, was identified as malicious by VirusTotal (58/72 engines) but created by the security agent. Suggesting anomalous benign intent, the file was remediated as escalator for further review based on shell executions.
                                        </p>
                                        <div className="pt-2 border-t border-white/[0.04] text-[10px] font-mono text-slate-500">
                                            Telemetry Confidence: 99.4%
                                        </div>
                                    </div>

                                </div>

                                {/* Threat Intelligence & OSINT Consensus Block */}
                                <div className="mt-4 p-4 rounded-xl bg-gradient-to-r from-blue-950/30 via-purple-950/20 to-slate-900/40 border border-white/[0.08] space-y-3">
                                    <div className="flex items-center justify-between">
                                        <div className="flex items-center gap-2">
                                            <ShieldAlert className="w-4 h-4 text-purple-400" />
                                            <span className="text-xs font-bold text-slate-100 uppercase tracking-wider font-mono">
                                                On-The-Fly Threat Intelligence (AbuseIPDB & VirusTotal)
                                            </span>
                                        </div>
                                        <span className="text-[10px] font-mono text-purple-400 bg-purple-500/10 px-2 py-0.5 rounded border border-purple-500/20">
                                            Live Cross-Correlation
                                        </span>
                                    </div>

                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-xs">
                                        {/* Target Destination Egress IP */}
                                        <div className="p-3 rounded-lg bg-black/40 border border-white/[0.04] space-y-1.5">
                                            <div className="flex items-center justify-between">
                                                <span className="text-[10px] text-slate-400 uppercase font-mono">Destination External IP</span>
                                                <span className="text-[9.5px] font-mono text-rose-400 bg-rose-500/10 px-1.5 py-0.2 rounded">EGRESS</span>
                                            </div>
                                            <div className="text-sm font-bold font-mono text-slate-100">
                                                <AbuseIPDBPopover ip={c.artifactsList?.find(a => a.type === 'Destination')?.ip || "207.35.49.174"} showBadge={true} className="text-blue-400 hover:underline">
                                                    {c.artifactsList?.find(a => a.type === 'Destination')?.ip || "207.35.49.174"}
                                                </AbuseIPDBPopover>
                                            </div>
                                            <span className="text-[10px] text-slate-500 block">Hover to view real-time AbuseIPDB confidence score & VirusTotal vendor detections.</span>
                                        </div>

                                        {/* Target Source Ingress IP */}
                                        <div className="p-3 rounded-lg bg-black/40 border border-white/[0.04] space-y-1.5">
                                            <div className="flex items-center justify-between">
                                                <span className="text-[10px] text-slate-400 uppercase font-mono">Source Ingress Endpoint</span>
                                                <span className="text-[9.5px] font-mono text-blue-400 bg-blue-500/10 px-1.5 py-0.2 rounded">INGRESS</span>
                                            </div>
                                            <div className="text-sm font-bold font-mono text-slate-100">
                                                <AbuseIPDBPopover ip={c.keyArtifacts?.device?.ip || "192.168.1.105"} showBadge={true} className="text-indigo-400 hover:underline">
                                                    {c.keyArtifacts?.device?.ip || "192.168.1.105"}
                                                </AbuseIPDBPopover>
                                            </div>
                                            <span className="text-[10px] text-slate-500 block">Internal network endpoint verified against telemetry base.</span>
                                        </div>
                                    </div>
                                </div>
                            </div>

                        </div>
                    )}

                    {/* ─── TAB 3: ARTIFACTS ─── */}
                    {activeTab === "artifacts" && (
                        <div className="space-y-4 animate-in fade-in duration-150">
                            {/* Live On-The-Fly IP / Hash Lookup Console */}
                            <div className="p-4 rounded-xl bg-black/40 border border-white/[0.08] space-y-3">
                                <div className="flex items-center justify-between">
                                    <span className="text-[11px] font-mono uppercase font-bold text-slate-300 flex items-center gap-1.5">
                                        <Search className="w-3.5 h-3.5 text-blue-400" />
                                        On-The-Fly Threat Intelligence Lookup (AbuseIPDB & VirusTotal)
                                    </span>
                                    <span className="text-[9.5px] font-mono text-slate-500">Instant Reputation Engine</span>
                                </div>
                                <div className="flex gap-2">
                                    <input
                                        type="text"
                                        placeholder="Enter external IP address (e.g. 185.220.101.5 or 40.93.19.2)..."
                                        value={osintIp}
                                        onChange={(e) => setOsintIp(e.target.value)}
                                        onKeyDown={(e) => e.key === 'Enter' && runOsintLookup()}
                                        className="flex-1 bg-[#090e18] border border-white/[0.1] rounded-lg px-3 py-1.5 text-xs text-white placeholder-slate-500 focus:outline-none focus:border-blue-500 font-mono"
                                    />
                                    <button
                                        onClick={() => runOsintLookup()}
                                        disabled={osintLoading}
                                        className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-xs font-bold font-mono transition-all flex items-center gap-1.5"
                                    >
                                        {osintLoading ? <RefreshCw className="w-3.5 h-3.5 animate-spin" /> : <Shield className="w-3.5 h-3.5" />}
                                        <span>{osintLoading ? "Querying..." : "Enrich IP"}</span>
                                    </button>
                                </div>

                                {/* Live Enrichment Result Card */}
                                {osintResult && (
                                    <div className="p-3 rounded-lg bg-[#070b14] border border-white/[0.08] grid grid-cols-2 md:grid-cols-4 gap-3 text-[11px] animate-in fade-in duration-200">
                                        <div>
                                            <span className="text-slate-500 text-[10px] uppercase font-mono block">Queried Target</span>
                                            <span className="text-white font-mono font-bold">{osintResult.ip}</span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500 text-[10px] uppercase font-mono block">AbuseIPDB Score</span>
                                            <span className={`font-mono font-bold ${osintResult.abuseConfidenceScore > 25 ? 'text-rose-400' : 'text-emerald-400'}`}>
                                                {osintResult.abuseConfidenceScore}% ({osintResult.totalReports} reports)
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500 text-[10px] uppercase font-mono block">VirusTotal Consensus</span>
                                            <span className={`font-mono font-bold ${osintResult.virusTotal?.malicious > 0 ? 'text-rose-400' : 'text-emerald-400'}`}>
                                                {osintResult.virusTotal?.detectionRate} Flagged
                                            </span>
                                        </div>
                                        <div>
                                            <span className="text-slate-500 text-[10px] uppercase font-mono block">ISP / Geo</span>
                                            <span className="text-slate-300 font-mono truncate block">{osintResult.isp} ({osintResult.countryCode})</span>
                                        </div>
                                    </div>
                                )}
                            </div>

                            <span className="text-[11px] font-mono uppercase text-slate-500 block">Correlated Case Entities</span>
                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                                {(c.artifactsList || []).map((art, idx) => (
                                    <div key={idx} className="p-3 bg-black/40 rounded-xl border border-white/[0.04] flex items-center justify-between text-[11.5px] group hover:border-white/10 transition-colors">
                                        <div>
                                            <span className="font-bold text-white block">{art.name}</span>
                                            <span className="text-[10px] text-slate-500 font-mono flex items-center gap-1 mt-0.5">
                                                <span>{art.type} •</span>
                                                {art.ip ? (
                                                    <AbuseIPDBPopover ip={art.ip} showBadge={true} className="text-blue-400 hover:underline">
                                                        {art.ip}
                                                    </AbuseIPDBPopover>
                                                ) : (
                                                    <span>{art.email || art.geo || art.hash}</span>
                                                )}
                                            </span>
                                        </div>
                                        <span className="px-2 py-0.5 bg-rose-500/20 text-rose-400 rounded text-[9px] font-mono font-bold">
                                            {art.risk}
                                        </span>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ─── TAB 4: INCIDENT & ALERTS ─── */}
                    {activeTab === "alerts" && (
                        <div className="space-y-2.5 animate-in fade-in duration-150">
                            {(c.alertsList || []).map((alt) => (
                                <div key={alt.id} className="p-3 bg-black/40 rounded-xl border border-white/[0.04] flex items-center justify-between text-[11.5px]">
                                    <div>
                                        <span className="text-white font-medium block">{alt.name}</span>
                                        <span className="text-[10px] font-mono text-slate-500">{alt.time} • FortiAnalyzer Ingest ID #ALT-9042</span>
                                    </div>
                                    <span className="px-2 py-0.5 bg-rose-500/20 text-rose-400 rounded text-[9px] font-mono font-bold">
                                        {alt.severity}
                                    </span>
                                </div>
                            ))}
                        </div>
                    )}

                    {/* ─── TAB 5: NOTES & EVIDENCE ─── */}
                    {activeTab === "notes" && (
                        <div className="space-y-4 animate-in fade-in duration-150">
                            <form onSubmit={handleAddNote} className="space-y-2">
                                <textarea
                                    rows="2"
                                    placeholder="Add investigator note or log finding..."
                                    value={newNote}
                                    onChange={(e) => setNewNote(e.target.value)}
                                    className="w-full bg-black/40 border border-white/[0.08] rounded-xl p-3 text-white text-[12px] outline-none focus:border-blue-500"
                                />
                                <button type="submit" className="px-4 py-1.5 bg-blue-600 hover:bg-blue-500 text-white rounded-lg text-[11px] font-bold font-mono">
                                    Add Note
                                </button>
                            </form>

                            <div className="space-y-2">
                                {(c.notes || []).map((nt) => (
                                    <div key={nt.id} className="p-3 bg-black/30 rounded-xl border border-white/[0.04] text-[11.5px] space-y-1">
                                        <div className="flex items-center justify-between text-[10px] font-mono text-slate-500">
                                            <span>{nt.author}</span>
                                            <span>{nt.time}</span>
                                        </div>
                                        <p className="text-slate-300">{nt.text}</p>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* ─── TAB 6: CASE HISTORY ─── */}
                    {activeTab === "history" && (
                        <div className="space-y-2 border-l border-white/[0.08] ml-2 pl-3 animate-in fade-in duration-150">
                            {(c.history || []).map((h, idx) => (
                                <div key={idx} className="space-y-0.5 relative py-1">
                                    <div className="absolute -left-[17px] top-2.5 w-2 h-2 rounded-full bg-blue-500" />
                                    <span className="text-[10px] font-mono text-slate-500">{h.time}</span>
                                    <p className="text-[11.5px] text-slate-300 font-mono">{h.event}</p>
                                </div>
                            ))}
                        </div>
                    )}

                </div>

            </div>
        </div>
    );
}
